import React from 'react'
import { Dropdown } from 'react-bootstrap'
import classNames from 'classnames'
import { type CrossClientQueryResult, CrossClientQueryResultMatches } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { selectCrossClientResultsExecution } from '../../../../store/crossClient/crossClientResults'
import { useChangeCrossClientQueryResultMatchMutation } from '../../../../store/crossClient/crossClient'
import { useCrossClientResultsRefresh } from '../../Results/useCrossClientResultsRefresh'
import { ReactComponent as MatchIcon } from '../../../../assets/images/cross-client-match.svg'
import { ReactComponent as NoMatchIcon } from '../../../../assets/images/cross-client-no-match.svg'
import './CrossClientChangeMatchButton.scss'

type ChangeableMatch = typeof CrossClientQueryResultMatches.MATCH | typeof CrossClientQueryResultMatches.NO_MATCH

interface ChangeMatchOption {
  match: ChangeableMatch
  label: string
  Icon: React.FC<{ width?: number | string; height?: number | string }>
}

const OPTIONS: ChangeMatchOption[] = [
  { match: CrossClientQueryResultMatches.MATCH, label: 'Match', Icon: MatchIcon },
  { match: CrossClientQueryResultMatches.NO_MATCH, label: 'No match', Icon: NoMatchIcon },
]

export interface CrossClientChangeMatchButtonProps {
  result: CrossClientQueryResult
  onChanged: (result: CrossClientQueryResult) => void
  className?: string
}

interface ChangeMatchToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  busy: boolean
}

const ChangeMatchToggle = React.forwardRef<HTMLButtonElement, ChangeMatchToggleProps>(({ busy, className, ...rest }, ref) => (
  <button
    ref={ref}
    type='button'
    disabled={busy}
    aria-disabled={busy}
    className={classNames(
      'cross-client-cta button-small d-inline-flex align-items-center gap-2 p-2 border rounded-2 bg-white zocks-border-0.5 zocks-border-color-gray-300 zocks-text-gray-900',
      className,
    )}
    {...rest}
  >
    <span>Change match</span>
    <i className='bi bi-chevron-expand' />
  </button>
))
ChangeMatchToggle.displayName = 'ChangeMatchToggle'

export const CrossClientChangeMatchButton: React.FC<CrossClientChangeMatchButtonProps> = ({ result, onChanged, className }) => {
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const [changeMatch, { isLoading }] = useChangeCrossClientQueryResultMatchMutation()
  const { refreshAfterResultMutation } = useCrossClientResultsRefresh()

  const handleSelect = async (target: ChangeableMatch) => {
    if (!execution || isLoading || result.match === target) {
      return
    }
    try {
      const updated = await changeMatch({ accountId, executionId: execution.id, resultId: result.id, match: target }).unwrap()
      onChanged(updated)
      await refreshAfterResultMutation()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Dropdown>
      <Dropdown.Toggle as={ChangeMatchToggle} bsPrefix='cross-client-change-match' busy={isLoading} className={className} />
      <Dropdown.Menu className='cross-client-change-match-menu p-1'>
        {OPTIONS.map((option) => {
          const isCurrent = result.match === option.match
          return (
            <Dropdown.Item
              key={option.match}
              disabled={isCurrent}
              onClick={() => void handleSelect(option.match)}
              className='d-flex align-items-center gap-2 paragraph-medium rounded-2 px-2 py-2'
            >
              <option.Icon width={16} height={16} />
              <span>
                Change to <span className='fw-600'>{option.label}</span>
              </span>
            </Dropdown.Item>
          )
        })}
      </Dropdown.Menu>
    </Dropdown>
  )
}
